var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList } from 'react-native';
import styled from 'styled-components/native';
import LeftArrowIcon from '@/assets/icon/ic_arrow_left.svg';
import ListIcon from '@/assets/icon/ic_list.svg';
import MapIcon from '@/assets/icon/ic_map.svg';
import { ScreenLayout } from '@/components/ScreenLayout';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import ItemMapView from '@/components/maps/ItemMapView';
import { toPlaceMarkerItem } from '@/components/maps/MarkerItem';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import useAppComponents from '@/hooks/useAppComponents';
import { usePlaceDetailScreenName } from '@/hooks/useFeatureFlags';
import SearchItemCard from '@/screens/SearchScreen/components/SearchItemCard';
import GeolocationUtils from '@/utils/GeolocationUtils';
const ConquestSearchItemCard = ({ item, onPress }) => (_jsx(SearchItemCard, { item: item, onPress: onPress, isConquestMode: true }));
const SearchUnconqueredPlacesScreen = ({ navigation, }) => {
    const { api } = useAppComponents();
    const mapRef = useRef(null);
    const [viewMode, setViewMode] = useState('map');
    const [cameraRegion, setCameraRegion] = useState(null);
    const [shouldShowRefresh, setShouldShowRefresh] = useState(false);
    const [queryTrigger, setQueryTrigger] = useState(0);
    // 재검색 시 사용할 카메라 영역 (재검색 버튼 눌렀을 때의 영역을 저장)
    const [searchRegion, setSearchRegion] = useState(null);
    const pdpScreen = usePlaceDetailScreenName();
    const { data, isLoading } = useQuery({
        queryKey: ['SearchUnconqueredPlaces', queryTrigger],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            // searchRegion이 있으면 rectangleRegion으로 검색, 없으면 현위치 기준 circleRegion으로 검색
            if (searchRegion) {
                const rectangleRegion = {
                    leftTopLocation: {
                        lat: searchRegion.northEast.latitude,
                        lng: searchRegion.southWest.longitude,
                    },
                    rightBottomLocation: {
                        lat: searchRegion.southWest.latitude,
                        lng: searchRegion.northEast.longitude,
                    },
                };
                const result = yield api.searchUnconqueredPlacesNearbyPost({
                    rectangleRegion,
                    limit: 50,
                });
                return result.data.items;
            }
            // 초기 검색: 현위치 기준 250m
            const position = yield GeolocationUtils.getCurrentPosition();
            const result = yield api.searchUnconqueredPlacesNearbyPost({
                circleRegion: {
                    currentLocation: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    },
                    distanceMetersLimit: 250,
                },
                limit: 50,
            });
            return result.data.items;
        }),
    });
    const items = useMemo(() => {
        var _a;
        return (_a = data === null || data === void 0 ? void 0 : data.map(toPlaceMarkerItem)) !== null && _a !== void 0 ? _a : [];
    }, [data]);
    useEffect(() => {
        if (items.length > 0 && !isLoading) {
            setTimeout(() => {
                var _a;
                (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.fitToItems(items);
            }, 300);
        }
    }, [items, isLoading]);
    const handleItemPress = useCallback((item) => {
        navigation.navigate(pdpScreen, {
            placeInfo: { placeId: item.place.id },
        });
    }, [pdpScreen, navigation]);
    const toggleViewMode = useCallback(() => {
        setViewMode(prev => (prev === 'map' ? 'list' : 'map'));
    }, []);
    const handleCameraIdle = useCallback((region) => {
        setCameraRegion(region);
        setShouldShowRefresh(true);
    }, []);
    const handleRefresh = useCallback(() => {
        // 현재 카메라 영역을 searchRegion으로 설정하고 재검색
        setSearchRegion(cameraRegion);
        setQueryTrigger(prev => prev + 1);
        setShouldShowRefresh(false);
    }, [cameraRegion]);
    return (_jsxs(Layout, { isHeaderVisible: false, safeAreaEdges: ['top'], children: [_jsxs(Header, { children: [_jsx(SccTouchableOpacity, { elementName: "search_unconquered_places_back_button", activeOpacity: 0.8, hitSlop: 10, onPress: () => navigation.goBack(), children: _jsx(LeftArrowIcon, { width: 24, height: 24, color: color.black }) }), _jsx(Title, { children: "\uC815\uBCF5 \uC548 \uB41C \uC7A5\uC18C" }), _jsx(ViewModeToggle, { elementName: "search_unconquered_places_view_mode_toggle", activeOpacity: 0.8, onPress: toggleViewMode, children: viewMode === 'map' ? (_jsxs(_Fragment, { children: [_jsx(ListIcon, { width: 20, height: 20 }), _jsx(ViewModeText, { children: "\uBAA9\uB85D" })] })) : (_jsxs(_Fragment, { children: [_jsx(MapIcon, { width: 20, height: 20 }), _jsx(ViewModeText, { children: "\uC9C0\uB3C4" })] })) })] }), _jsxs(ContentContainer, { children: [_jsx(MapContainer, { children: _jsx(ItemMapView, { ref: mapRef, items: items, ItemCard: ConquestSearchItemCard, isRefreshVisible: shouldShowRefresh, onRefresh: handleRefresh, onCameraIdle: handleCameraIdle }) }), viewMode === 'list' && (_jsx(ListContainer, { children: _jsx(FlatList, { data: items, keyExtractor: item => item.place.id, contentContainerStyle: { paddingBottom: 100 }, renderItem: ({ item }) => (_jsx(ListItemWrapper, { children: _jsx(SearchItemCard, { item: item, isHeightFlex: true, isConquestMode: true, onPress: () => handleItemPress(item) }) })) }) }))] })] }));
};
const Layout = styled(ScreenLayout) `
  background-color: ${color.white};
`;
const ContentContainer = styled.View `
  flex: 1;
`;
const MapContainer = styled.View `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;
const ListContainer = styled.View `
  flex: 1;
  background-color: ${color.white};
`;
const Header = styled.View `
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-vertical: 10px;
  padding-horizontal: 20px;
  gap: 12px;
  height: 64px;
`;
const ViewModeToggle = styled(SccTouchableOpacity) `
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 38px;
`;
const ViewModeText = styled.Text `
  font-size: 12px;
  font-family: ${font.pretendardRegular};
  color: ${color.black};
`;
const ListItemWrapper = styled.View `
  padding: 20px;
  border-top-width: 1px;
  border-top-color: ${color.gray20};
`;
const Title = styled.Text `
  font-size: 18px;
  font-family: ${font.pretendardBold};
  color: ${color.black};
`;
export default SearchUnconqueredPlacesScreen;
